import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiCall } from '../utils/api.js'

export const useProjectStore = defineStore('projects', () => {
  // Estado
  const projects = ref([])
  const allProjects = ref([]) // Tutti i progetti senza filtro per i summary
  const loading = ref(false)
  const error = ref(null)
  const currentProject = ref(null)
  const statusFilter = ref(null)
  const projectTypeFilter = ref(null)
  const groupFilter = ref(null)
  const configurations = ref({
    config: null,
    fields: null
  })

  // Paginação
  const currentPage = ref(1)
  const pageSize = ref(50)
  
  // Computed
  const totalProjects = computed(() => filteredProjects.value.length)

  const filteredProjects = computed(() => {
    // Se non è selezionato alcun tipo di progetto, non mostrare nulla
    if (projectTypeFilter.value === null || projectTypeFilter.value === undefined) {
      return []
    }

    let filtered = projects.value
    
    // Filtra per tipo di progetto
    filtered = filtered.filter(project => parseInt(project.projectType) === projectTypeFilter.value)
    
    // Filtra per status
    if (statusFilter.value !== null && statusFilter.value !== undefined) {
      filtered = filtered.filter(project => parseInt(project.activeStatus) === parseInt(statusFilter.value))
    } else if (groupFilter.value !== null && groupFilter.value !== undefined) {
      // Filtra per gruppo: prende tutti gli status del tipo corrente che appartengono al gruppo
      const statusListForType = projectTypes.value.find(pt => pt.id === projectTypeFilter.value)?.statusList || []
      const groupStatusIds = statusListForType
        .filter(s => s.group === groupFilter.value)
        .map(s => s.id)
      filtered = filtered.filter(project => groupStatusIds.includes(parseInt(project.activeStatus)))
    }
    
    return filtered
  })

  const statusList = computed(() => {
    // Se c'è un filtro per tipo di progetto, ritorna gli stati di quel tipo
    if (projectTypeFilter.value !== null) {
      const projectType = projectTypes.value.find(pt => pt.id === projectTypeFilter.value)
      return projectType?.statusList || []
    }
    // Altrimenti ritorna tutti gli stati di tutti i tipi (per compatibilità)
    const allStatuses = []
    const seenIds = new Set()
    projectTypes.value.forEach(pt => {
      pt.statusList?.forEach(status => {
        if (!seenIds.has(status.id)) {
          seenIds.add(status.id)
          allStatuses.push(status)
        }
      })
    })
    return allStatuses
  })

  const projectTypes = computed(() => {
    return configurations.value.config?.projectTypes || []
  })

  const fields = computed(() => {
    return configurations.value.fields || {}
  })

  const fieldRules = computed(() => {
    return configurations.value.fields || {}
  })

  const instructions = computed(() => {
    return configurations.value.instructions || {}
  })

  // Actions
  async function loadConfigurations() {
    try {
      loading.value = true
      const response = await apiCall('/api/projects/configs')
      configurations.value = response
    } catch (err) {
      error.value = 'Erro ao carregar configurações'
      console.error('Erro ao carregar configurações:', err)
    } finally {
      loading.value = false
    }
  }

  async function loadProjects(page = 1, status = null, silent = false) {
    try {
      if (!silent) {
        loading.value = true
      }
      error.value = null
      
      // Carica sempre tutti i progetti senza filtro status sul server
      // Il filtro status verrà applicato solo localmente tramite computed
      let endpoint = `/api/projects?page=1&pageSize=1000`
      
      const data = await apiCall(endpoint)

      projects.value = data.projects
      allProjects.value = data.projects // Usa la stessa lista per i summary
      currentPage.value = 1 // Sempre pagina 1 dato che carichiamo tutto
    } catch (err) {
      if (!silent) {
        error.value = 'Erro ao carregar projetos'
      }
      console.error('Erro ao carregar projetos:', err)
    } finally {
      if (!silent) {
        loading.value = false
      }
    }
  }

  async function getProject(projectId) {
    try {
      loading.value = true
      const data = await apiCall(`/api/projects/${projectId}`)
      currentProject.value = data
      return data
    } catch (err) {
      error.value = 'Erro ao carregar projeto'
      console.error('Erro ao carregar projeto:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  async function createProject(projectData) {
    try {
      loading.value = true
      error.value = null
      
      const newProject = await apiCall('/api/projects', {
        method: 'POST',
        body: JSON.stringify(projectData)
      })
      
      // Cria la cartella del progetto nel file system locale PRIMA di aggiungere alla lista
      try {
        await createProjectFolder(newProject.idProject, projectData.client)
      } catch (folderError) {
        // Se la creazione della cartella fallisce, elimina il progetto dal backend
        try {
          await apiCall(`/api/projects/${newProject.idProject}`, {
            method: 'DELETE'
          })
        } catch (deleteError) {
          console.error('Failed to rollback project creation:', deleteError)
        }
        throw new Error('Falha ao criar a pasta do projeto: ' + (folderError.message || 'Erro desconhecido'))
      }
      
      // Salva info.txt nella cartella del progetto
      try {
        await saveProjectInfoFile(newProject)
      } catch (infoError) {
        console.warn('Failed to save info.txt:', infoError)
        // Non blocchiamo per questo errore
      }
      
      projects.value.unshift(newProject)
      totalProjects.value += 1
      
      return newProject
    } catch (err) {
      error.value = 'Erro ao criar projeto'
      console.error('Erro ao criar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function createProjectFolder(projectId, clientName) {
    // Carica la configurazione dal LocalBackend
    const pathsConfig = await apiCall('/api/config/config', {
      method: 'GET',
      backend: 'local',
      includeAuth: false
    })
    
    const localPath = pathsConfig?.localPath
    
    if (!localPath) {
      throw new Error('Caminho local não configurado')
    }

    // Chiama il LocalBackend per creare la cartella
    const result = await apiCall('/api/projectfolders/create', {
      method: 'POST',
      body: JSON.stringify({
        basePath: localPath,
        projectId: projectId,
        clientName: clientName || ''
      }),
      backend: 'local',
      includeAuth: false
    })

    console.log('Project folder created:', result)
    return result
  }

  async function openProjectFolder(projectId) {
    try {
      // Carica la configurazione dal LocalBackend
      const pathsConfig = await apiCall('/api/config/config', {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })
      
      const localPath = pathsConfig?.localPath
      
      if (!localPath) {
        throw new Error('Local path not configured')
      }

      const result = await apiCall('/api/projectfolders/open', {
        method: 'POST',
        body: JSON.stringify({
          basePath: localPath,
          projectId: projectId
        }),
        backend: 'local',
        includeAuth: false
      })

      return result
    } catch (err) {
      console.error('Error opening project folder:', err)
      throw err
    }
  }

  async function openArchivedProjectFolder(projectId) {
    try {
      // Carica la configurazione dal LocalBackend
      const pathsConfig = await apiCall('/api/config/config', {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })
      
      const archivePath = pathsConfig?.archivePath
      
      if (!archivePath) {
        throw new Error('Archive path not configured')
      }

      const result = await apiCall('/api/projectfolders/open', {
        method: 'POST',
        body: JSON.stringify({
          basePath: archivePath,
          projectId: projectId
        }),
        backend: 'local',
        includeAuth: false
      })

      return result
    } catch (err) {
      console.error('Error opening archived project folder:', err)
      throw err
    }
  }

  async function checkProjectFolder(projectId) {
    try {
      // Carica la configurazione dal LocalBackend
      const pathsConfig = await apiCall('/api/config/config', {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })
      
      const localPath = pathsConfig?.localPath
      
      if (!localPath) {
        throw new Error('Local path not configured')
      }

      const result = await apiCall(`/api/projectfolders/check?basePath=${encodeURIComponent(localPath)}&projectId=${encodeURIComponent(projectId)}`, {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })

      return result.exists
    } catch (err) {
      console.error('Error checking project folder:', err)
      return false
    }
  }

  async function saveProjectInfoFile(projectData) {
    try {
      // Carica la configurazione dal LocalBackend
      const pathsConfig = await apiCall('/api/config/config', {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })
      
      const localPath = pathsConfig?.localPath
      
      if (!localPath) {
        console.warn('Local path not configured, skipping info.txt save')
        return
      }

      // Converte i dati del progetto in JSON formattato
      const jsonData = JSON.stringify(projectData, null, 2)

      // Chiama il LocalBackend per salvare info.txt
      await apiCall('/api/projectfolders/save-info', {
        method: 'POST',
        body: JSON.stringify({
          basePath: localPath,
          projectId: projectData.idProject,
          jsonData: jsonData
        }),
        backend: 'local',
        includeAuth: false
      })

      console.log('Project info.txt saved successfully')
    } catch (err) {
      console.warn('Warning: Could not save info.txt to local folder. LocalBackend may be offline.', err)
      // Non bloccare l'operazione, solo mostra un warning
    }
  }

  async function updateProject(projectId, projectData) {
    try {
      loading.value = true
      error.value = null
      
      const updatedProject = await apiCall(`/api/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(projectData)
      })
      
      const index = projects.value.findIndex(p => p.idProject === projectId)
      if (index !== -1) {
        projects.value[index] = updatedProject
      }
      
      if (currentProject.value && currentProject.value.idProject === projectId) {
        currentProject.value = updatedProject
      }
      
      // Salva info.txt nella cartella del progetto
      await saveProjectInfoFile(updatedProject)
      
      return updatedProject
    } catch (err) {
      // Check if it's a concurrency conflict
      if (err.response?.status === 409 || err.error === 'CONCURRENCY_CONFLICT') {
        const concurrencyError = new Error(err.message || 'Conflitto di concorrenza: il progetto è stato modificato da un altro utente.')
        concurrencyError.isConcurrencyConflict = true
        concurrencyError.details = err
        throw concurrencyError
      }
      
      error.value = 'Erro ao atualizar projeto'
      console.error('Erro ao atualizar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function deleteProject(projectId) {
    try {
      loading.value = true
      error.value = null
      
      await apiCall(`/api/projects/${projectId}`, {
        method: 'DELETE'
      })
      
      projects.value = projects.value.filter(p => p.idProject !== projectId)
      totalProjects.value -= 1
      
      if (currentProject.value && currentProject.value.idProject === projectId) {
        currentProject.value = null
      }
    } catch (err) {
      error.value = 'Erro ao deletar projeto'
      console.error('Erro ao deletar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function getProjectHistory(projectId) {
    try {
      loading.value = true
      error.value = null
      
      const history = await apiCall(`/api/projects/${projectId}/history`)
      return history
    } catch (err) {
      error.value = 'Erro ao carregar histórico do projeto'
      console.error('Erro ao carregar histórico:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function archiveProject(projectId) {
    try {
      loading.value = true
      error.value = null
      
      // Passo 1: Ottieni la configurazione locale
      const pathsConfig = await apiCall('/api/config/config', {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })
      
      const localPath = pathsConfig?.localPath
      
      if (!localPath) {
        throw new Error('Caminho local não configurado')
      }

      // Passo 2: Sposta la cartella nell'archivio tramite LocalBackend
      try {
        await apiCall('/api/projectfolders/archive', {
          method: 'POST',
          body: JSON.stringify({
            basePath: localPath,
            projectId: projectId
          }),
          backend: 'local',
          includeAuth: false
        })
      } catch (folderError) {
        throw new Error('Falha ao mover a pasta do projeto para o arquivo: ' + (folderError.message || 'Erro desconhecido'))
      }
      
      console.log('Project folder archived in local backend')
      
      // Passo 3: Archivia il progetto nel Backend (sposta JSON)
      try {
        await apiCall(`/api/projects/${projectId}/archive`, {
          method: 'POST'
        })
      } catch (backendError) {
        // Se fallisce l'archiviazione nel backend, potremmo voler fare rollback della cartella
        // ma per ora solo loghiamo l'errore
        console.error('Failed to archive project in backend after moving folder:', backendError)
        throw new Error('Falha ao arquivar projeto no servidor: ' + (backendError.message || 'Erro desconhecido'))
      }
      
      console.log('Project archived in backend')
      
      // Rimuovi il progetto dalla lista
      projects.value = projects.value.filter(p => p.idProject !== projectId)
      totalProjects.value -= 1
      
      if (currentProject.value && currentProject.value.idProject === projectId) {
        currentProject.value = null
      }
      
      return { success: true }
    } catch (err) {
      error.value = err.message || 'Erro ao arquivar projeto'
      console.error('Erro ao arquivar projeto:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  function setStatusFilter(status) {
    statusFilter.value = status
    // Non ricarichiamo più i progetti, solo cambiamo il filtro
    // che verrà applicato tramite filteredProjects computed
  }

  function setGroupFilter(group) {
    groupFilter.value = group
  }

  function setProjectTypeFilter(projectType) {
    projectTypeFilter.value = projectType
  }

  function clearError() {
    error.value = null
  }

  function clearCurrentProject() {
    currentProject.value = null
  }

  // Verificar se campo é obrigatório para um status específico
  function isFieldRequired(fieldName, statusNumber) {
    const field = fieldRules.value[fieldName]
    if (!field) return false
    // Se mandatoryStatusNumber não está definido, o campo não é obrigatório
    if (field.mandatoryStatusNumber === undefined) return false
    return field.mandatoryStatusNumber <= statusNumber
  }

  // Verificar se campo é visível para um status específico
  function isFieldVisible(fieldName, statusNumber) {
    const field = fieldRules.value[fieldName]
    if (!field) return true
    // Se visibleInStatusNumber não está definido, o campo é sempre visível
    if (field.visibleInStatusNumber === undefined) return true
    return field.visibleInStatusNumber <= statusNumber
  }

  // Verificar se campo é readonly
  function isFieldReadonly(fieldName) {
    const field = fieldRules.value[fieldName]
    if (!field) return false
    return field.readonly === true
  }

  // Obter informações do status por ID
  function getStatusInfo(statusId, projectTypeId = null) {
    if (statusId === null || statusId === undefined) return null
    
    // Se specificato il tipo, cerca negli stati di quel tipo
    if (projectTypeId !== null && projectTypeId !== undefined) {
      const projectType = projectTypes.value.find(pt => pt.id.toString() === projectTypeId.toString())
      const statusInfo = projectType?.statusList?.find(status => status.id.toString() === statusId.toString())
      
      // Se lo stato ha instructionKey, aggiungi le instructions dal file separato
      if (statusInfo && statusInfo.instructionKey) {
        const [typeKey, statusKey] = statusInfo.instructionKey.split('.')
        statusInfo.instructions = instructions.value[typeKey]?.[statusKey] || ''
      }
      
      return statusInfo
    }
    // Altrimenti cerca in tutti gli stati
    const statusInfo = statusList.value.find(status => status.id.toString() === statusId.toString())
    
    // Se lo stato ha instructionKey, aggiungi le instructions dal file separato
    if (statusInfo && statusInfo.instructionKey) {
      const [typeKey, statusKey] = statusInfo.instructionKey.split('.')
      statusInfo.instructions = instructions.value[typeKey]?.[statusKey] || ''
    }
    
    return statusInfo
  }

  // Obter lista de status para um tipo de projeto específico
  function getStatusListForType(projectTypeId) {
    if (projectTypeId === null || projectTypeId === undefined) return []
    const projectType = projectTypes.value.find(pt => pt.id.toString() === projectTypeId.toString())
    return projectType?.statusList || []
  }

  // Obter informações do tipo de projeto por ID
  function getProjectTypeInfo(typeId) {
    if (typeId === null || typeId === undefined) return null
    return projectTypes.value.find(type => type.id.toString() === typeId.toString())
  }

  return {
    // Estado
    projects,
    allProjects,
    loading,
    error,
    currentProject,
    statusFilter,
    projectTypeFilter,
    groupFilter,
    configurations,
    currentPage,
    pageSize,
    totalProjects,
    
    // Computed
    filteredProjects,
    statusList,
    projectTypes,
    fields,
    fieldRules,
    instructions,
    
    // Actions
    loadConfigurations,
    loadProjects,
    getProject,
    createProject,
    updateProject,
    deleteProject,
    getProjectHistory,
    archiveProject,
    setStatusFilter,
    setProjectTypeFilter,
    setGroupFilter,
    clearError,
    clearCurrentProject,
    createProjectFolder,
    openProjectFolder,
    openArchivedProjectFolder,
    checkProjectFolder,
    saveProjectInfoFile,
    
    // Helpers
    isFieldRequired,
    isFieldVisible,
    isFieldReadonly,
    getStatusInfo,
    getStatusListForType,
    getProjectTypeInfo
  }
})